import React, { useEffect, useMemo, useState } from 'react';
import { ServiceInfo } from './model/ServiceInfo';
import { BookingAPI } from './api/BookingApi';
import { Calendar, CalendarDayInfo, TimeSlot } from './model/Calendar';
import { CalendarWidget } from './widgets/CalendarWidget';
import { ClientDetails, ReservationSummary } from './model/ReservationDetails';
import { PhoneNumberInput } from './widgets/PhoneNumberWidget';

function ServiceSelector({ onSelect }: { 
    onSelect: (x: ServiceInfo) => void
})
{
    let api = useMemo(() => BookingAPI.get(), []);
    const [services, setServices] = useState<ServiceInfo[] | null>(null);
    
    useEffect(() => {
        api.getServices().then(services => setServices(services));
    }, [api]);

    return <div className="service-selector reserve-part">
                <h3>Rezerwuj</h3>
                { services ? 
                    <table className="reserve-tbl">
                    {
                        services.map(service => 
                            <React.Fragment>
                            <tr>
                                <td className="desc-cell">{ service.name }</td>
                                <td className="price-cell">{ service.price }&nbsp;zł</td>
                                <td><button onClick={() => onSelect(service)} className="reserve-btn">Umów</button></td>
                            </tr>
                            <tr>
                                <td colSpan={3} style={({marginTop: 0, paddingTop: 0, borderBottom: '1px solid black'})}></td>
                            </tr>
                            </React.Fragment>
                        )
                    }
                    </table> :
                    'Wczytywanie...'
                }
    </div>
}

function format_time(time: string)
{
    let data = time.split(':');
    return data[0] + ':' + data[1];
}

function AppointmentSelector({ service, onSelect }: { 
    onSelect: (date: string, time_slot: TimeSlot) => void,
    service: ServiceInfo
})
{
    let api = useMemo(() => BookingAPI.get(), []);
    const [calendar, setCalendar] = useState<Calendar | null>(null);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedCalendarCell, setSelectedCalendarCell] = useState<CalendarDayInfo | null>(null);
    const [currentTimeSlotId, setCurrentTimeSlotId] = useState<number | null>(null);

    useEffect(() => {
        let year = currentMonth.getFullYear();
        let month = currentMonth.getMonth() + 1;
        api.getCalendar(service.id, year, month).then(cal => setCalendar(cal));
    }, [api, service, currentMonth]);

    let currentTimeSlot = selectedCalendarCell?.data.find(t => t.id === currentTimeSlotId);

    function onPrevMonth() {
        let year = currentMonth.getFullYear();
        let month = currentMonth.getMonth() - 1;

        if (month < 0) {
            month = 11;
            year--;
        }

        setCurrentMonth(new Date(year, month, 1));
    }

    function onNextMonth() {
        let year = currentMonth.getFullYear();
        let month = currentMonth.getMonth() + 1;

        if (month == 12) {
            month = 0;
            year++;
        }

        setCurrentMonth(new Date(year, month, 1));
    }

    function isDateEnabled(date_data: CalendarDayInfo)
    {
        function only_date(date: Date)
        {
            return new Date(date.getFullYear(), date.getMonth(), date.getDate());
        }
        let date = only_date(new Date(date_data.date));
        let now_date = only_date(new Date());

        if (date <= now_date)
        {
            return false;
        }
        return date_data.data.length > 0;
    }

    function onSelectDate(date_data: CalendarDayInfo)
    {
        setSelectedCalendarCell(date_data);
    }

    function onTimeSlotSelect(slotId: number)
    {
        setCurrentTimeSlotId(slotId);
    }

    function onAppointmentSelect()
    {
        if (selectedCalendarCell && currentTimeSlot)
        {
            onSelect(selectedCalendarCell.date, currentTimeSlot);
        }
    }

    return <div className='reserve-part'>{ 
        calendar ? 
            <CalendarWidget selected={selectedCalendarCell} onPrevMonth={onPrevMonth} onNextMonth={onNextMonth} calendar={calendar} isDateEnabled={isDateEnabled} onSelect={onSelectDate}></CalendarWidget>
            : "Wczytywanie"
        }
        
        <div>
            { selectedCalendarCell && <select className="time-slot-selector" onChange={e => onTimeSlotSelect(parseInt(e.target.value))}>
                <option value={0}>-----</option>
                { selectedCalendarCell.data.map(r => {
                    let start_time = format_time(r.start_time);
                    let end_time = format_time(r.end_time);

                    return <option key={r.id} value={r.id} selected={r.id == currentTimeSlotId}>{ start_time } - { end_time }</option>
                }) }
            </select>}
        </div>

        <button onClick={onAppointmentSelect} disabled={!selectedCalendarCell || !currentTimeSlot}>
            Wybierz termin
        </button>
    </div>
}

function ContactDetails({ details, onChange, onReserve, }: { details: ClientDetails, onChange: (details: ClientDetails) => void, onReserve: () => void })
{
    let [errors, setErrors] = useState<{ [key in keyof ClientDetails]?: string }>({});

    function onFieldChange(value: string, fieldName: keyof ClientDetails)
    {
        details = {
            ...details,
            [fieldName]: value
        }

        if (errors[fieldName])
        {
            setErrors({
                ...errors,
                [fieldName]: null,
            });
        }
        onChange(details);
    }

    function onReserveClick() {
        let validation_errors = {
            ...errors
        }

        let has_any_errors = false;
        if (!details.client_name)
        {
            validation_errors['client_name'] = 'Proszę podać imię i nazwisko';
            has_any_errors = true;
        }

        if (details.client_phone_number.length < 9 || details.client_phone_number.indexOf('_') !== -1)
        {
            validation_errors['client_phone_number'] = 'Proszę podać poprawny numer telefonu';
            has_any_errors = true;
        }

        if (details.client_email && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(details.client_email))
        {
            validation_errors['client_email'] = 'Proszę podać poprawny adres email';
            has_any_errors = true;
        }

        setErrors(validation_errors);

        if (!has_any_errors)
        {
            onReserve();
        }
    }

    return <div className="reserve-part">
        <label htmlFor="id_client_name">Imię i nazwisko</label><input id="id_client_name" value={details.client_name} onChange={e => onFieldChange(e.target.value, 'client_name')} />
        { errors['client_name'] && <span className="error">* { errors['client_name'] }</span> } <br />
        <label htmlFor="id_phone_number">Numer telefonu</label><PhoneNumberInput id="id_phone_number" value={details.client_phone_number} onChange={value => onFieldChange(value, 'client_phone_number')} />
        { errors['client_phone_number'] && <span className="error">* { errors['client_phone_number'] }</span> }<br />
        <label htmlFor="id_email">Email</label><input id="id_email" value={details.client_email} onChange={e => onFieldChange(e.target.value, 'client_email')} />
        { errors['client_email'] && <span className="error">* { errors['client_email'] }</span> }<br />
        <button onClick={onReserveClick}>Rezerwuj</button>
    </div>
}

export function ReservationSummaryTable({ summary }: { summary: ReservationSummary })
{
    if (summary.redirect_url)
    {
        window.location.href = summary.redirect_url;
        return <div></div>;
    }

    return <div className="reservation-summary reserve-part">
            <h3>Podsumowanie rezerwacji</h3>
            <table>
                <tr>
                    <td>Usługa</td>
                    <td>{ summary.service_name }</td>
                </tr>
                <tr>
                    <td>Cena</td>
                    <td>{ summary.service_price } zł</td>
                </tr>
                <tr>
                    <td>Termin</td>
                    <td>{ summary.date } { summary.time_slot_desc }</td>
                </tr>
                <tr>
                    <td>Imię i nazwisko</td>
                    <td>{ summary.client_name }</td>
                </tr>
                <tr>
                    <td>Telefon</td>
                    <td>{ summary.client_phone }</td>
                </tr>
                <tr>
                    <td>Email</td>
                    <td>{ summary.client_email }</td>
                </tr>
            </table>
        </div>
}

export function ReserveWizzard()
{
    const api = useMemo(() => BookingAPI.get(), []);
    const [selectedService, setSelectedService] = useState<ServiceInfo>({ id: 0, name: '', price: 0 });
    const [appointment, setAppointment] = useState<{
        date: string,
        time_slot: CalendarDayInfo['data'][0],
    } | null>(null);
    const [clientDetails, setClientDetails] = useState<ClientDetails>({
        client_name: '',
        client_phone_number: '+48 ',
        client_email: '',
    });
    const [registrationState, setRegistrationState] = useState<'service' | 'appointment' | 'client' | 'success'>('service');
    const [regisrationSummary, setRegistrationSummary] = useState<ReservationSummary | null>(null);

    if (registrationState === 'service')
    {
        return (
            <div>
                <ServiceSelector onSelect={service => {
                    setSelectedService(service);
                    setRegistrationState('appointment');
                }}></ServiceSelector>
            </div>
        )
    }

    if (registrationState === 'appointment')
    {
    return (
        <div className="appointment-selector">
                <h3>{ selectedService.name }: Rezerwacja</h3>

                <p>
                    Koszt: { selectedService.price } zł
                </p>

                <AppointmentSelector service={selectedService} onSelect={(date, time_slot) => {
                    setRegistrationState('client');
                    setAppointment({
                        date: date,
                        time_slot: time_slot,
                    })
                }}></AppointmentSelector>
        
                
        </div>
        )
    }

    
    async function onReserve()
    {
        let res = await api.reserve({
            service_id: selectedService!.id,
            date: appointment!.date,
            time_slot_id: appointment!.time_slot.id,
            client_name: clientDetails!.client_name,
            client_phone_number: clientDetails!.client_phone_number,
            client_email: clientDetails!.client_email,
        })

        if (res)
        {
            setRegistrationState('success');
            setRegistrationSummary(res);
        }
    }

    if (registrationState === 'client')
    {
        return <ContactDetails details={clientDetails} onChange={setClientDetails} onReserve={onReserve} />
    }

    if (regisrationSummary)
    {
        return <ReservationSummaryTable summary={regisrationSummary} />
    }

    return null;
}